import { useCallback, useEffect, useRef, useState } from 'react';
import { BATTLE_HEALTH_BARRIER_THRESHOLDS } from '../constants/battleHealthHud';

/**
 * Computes which barrier thresholds are dismantled per game rules (matches PlayerHUD / OpponentHUD).
 */
export function computeActualBarrierDismantledSet(health, barriersRemoved) {
  const healthNum = Number(health);
  const h = Number.isFinite(healthNum) ? healthNum : 0;
  const removed = Array.isArray(barriersRemoved) ? barriersRemoved : [];
  const set = new Set();
  for (const threshold of BATTLE_HEALTH_BARRIER_THRESHOLDS) {
    if (h <= threshold || removed.includes(threshold)) {
      set.add(threshold);
    }
  }
  return set;
}

/**
 * Barrier hex / sprite dismantled state is shown `delayMs` after authoritative state marks it dismantled,
 * so audio can lead the visual. Changing `resetKey` (e.g. game id) snaps display to actual immediately.
 *
 * @param {number} delayMs – pass `0` to disable delay for a given HUD instance.
 */
const BARRIER_DISPLAY_NEVER_SYNCED = Symbol('barrier-display-never-synced');

export function useDelayedBarrierSpriteDisplay(health, barriersRemoved, delayMs, resetKey) {
  const [displayGeneration, setDisplayGeneration] = useState(0);
  const displayRef = useRef(new Set());
  const timeoutsRef = useRef(new Map());
  /** Tracks which battle session (`resetKey`) we've synced; sentinel until first sync. */
  const lastSyncedKeyRef = useRef(BARRIER_DISPLAY_NEVER_SYNCED);

  const bump = () => setDisplayGeneration((g) => g + 1);

  useEffect(() => {
    return () => {
      for (const id of timeoutsRef.current.values()) {
        clearTimeout(id);
      }
      timeoutsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const actual = computeActualBarrierDismantledSet(health, barriersRemoved);

    const battleContextChanged =
      lastSyncedKeyRef.current === BARRIER_DISPLAY_NEVER_SYNCED ||
      lastSyncedKeyRef.current !== resetKey;

    if (battleContextChanged) {
      lastSyncedKeyRef.current = resetKey;
      for (const id of timeoutsRef.current.values()) {
        clearTimeout(id);
      }
      timeoutsRef.current.clear();
      displayRef.current = new Set(actual);
      bump();
      return;
    }

    for (const threshold of BATTLE_HEALTH_BARRIER_THRESHOLDS) {
      if (!actual.has(threshold)) {
        const tid = timeoutsRef.current.get(threshold);
        if (tid !== undefined) {
          clearTimeout(tid);
          timeoutsRef.current.delete(threshold);
        }
        if (displayRef.current.delete(threshold)) {
          bump();
        }
      }
    }

    for (const threshold of actual) {
      if (displayRef.current.has(threshold)) continue;

      if (delayMs <= 0) {
        displayRef.current.add(threshold);
        bump();
        continue;
      }

      if (timeoutsRef.current.has(threshold)) continue;

      const tid = setTimeout(() => {
        timeoutsRef.current.delete(threshold);
        displayRef.current.add(threshold);
        bump();
      }, delayMs);
      timeoutsRef.current.set(threshold, tid);
    }
  }, [health, barriersRemoved, delayMs, resetKey]);

  const isBarrierSpriteDismantled = useCallback(
    (threshold) => displayRef.current.has(threshold),
    [displayGeneration]
  );

  return { isBarrierSpriteDismantled };
}
